import React, {useMemo, useState} from "react"
import moment from "moment"
import {handleError, roundTwoDigits} from "helpers"
import SummaryView from "./SummaryView"
import {BaseRangePicker} from "components/DateTimePicker"
import styles from "./DailyAttendanceCalendarView.module.css"
import cx from "classnames"
import {attendanceService} from "services"
import {useQuery} from "@tanstack/react-query"
import {BasePopup} from "uiKit"
import {BaseButton, BaseInput, BaseLoading, SecondaryButton} from "components"
import {Col, InputNumber, Row} from "antd"
import {FormLabel} from "components/Form"
import {BaseDatePicker} from "components/DateTimePicker"
import {useCurrentProfilePermissions} from "hooks"
import {Permissions} from "@edular/permissions"

// Helper to create an array of month/year pairs between two dates
function getMonthsInRange(startDate, endDate) {
    const start = moment(startDate).startOf("month")
    const end = moment(endDate).startOf("month")
    const months = []
    let current = start.clone()
    while (current.isSameOrBefore(end)) {
        months.push(current.clone())
        current.add(1, "month")
    }
    return months
}

// Build days grid for a month (nulls for empty cells before 1st day)
function buildMonthDays(momentMonth) {
    const daysInMonth = momentMonth.daysInMonth()
    const startWeekday = momentMonth.startOf("month").day()

    const days = Array(startWeekday).fill(null) // empty slots for grid alignment
    for (let d = 1; d <= daysInMonth; d++) days.push(d)
    return days
}

// Format date like "YYYY-MM-DD" for event matching
function formatDateOnly(date) {
    return moment(date).format("YYYY-MM-DD")
}

const DailyAttendanceCalendarView = (payload: {studentProfileId: any; campusId: number; timezone?: string}) => {
    const {studentProfileId, campusId, timezone = moment.tz.guess()} = payload

    const [dateRange, setDateRange] = useState(
        timezone ? [moment().tz(timezone).subtract(6, "days"), moment().tz(timezone)] : []
    )
    const [hoveredDateStr, setHoveredDateStr] = useState<string | null>(null)
    const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null)
    const [isEditPopupVisible, setIsEditPopupVisible] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editValues, setEditValues] = useState({
        date: null,
        missedHours: 0,
        distEdHours: 0,
        makeUpHours: 0,
        attendedHours: 0,
        scheduleHours: 0
    })
    const currentProfilePermissions = useCurrentProfilePermissions()
    const canEdit = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
    })
    const canAdd = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    const monthsInRange = useMemo(() => {
        if (dateRange?.length !== 2) return []
        return getMonthsInRange(dateRange[0], dateRange[1])
    }, [dateRange])

    const getFilterParams = () => {
        const params: any = {
            studentProfileId
        }

        if (dateRange?.length) {
            const startDate = moment(dateRange[0])
            startDate.startOf("day")
            const endDate = moment(dateRange[1])
            endDate.endOf("day")
            params.fromDate = startDate.format("YYYY-MM-DD")
            params.toDate = endDate.format("YYYY-MM-DD")
        }

        return params
    }

    const {
        data: dailyAttendanceSummaryData,
        refetch,
        isFetching
    } = useQuery(
        ["attendance-summary-calendar-report", dateRange, studentProfileId],
        async () => {
            const params = getFilterParams()
            const {data} = await attendanceService.getDailyAttendanceSummaryCalendarReport(params)
            return data
        },
        {initialData: [], enabled: !!dateRange?.length && !!studentProfileId}
    )

    // Process events grouped by date for fast lookup
    const eventsByDate = useMemo(() => {
        const dailyAttendances = dailyAttendanceSummaryData.dailyAttendances ?? []

        const map = {}
        for (const attendance of dailyAttendances) {
            const dateStr = formatDateOnly(attendance.date)
            if (!map[dateStr]) map[dateStr] = []

            map[dateStr].push({title: `Missed Hours: ${roundTwoDigits(attendance.missedHours ?? 0)}`})
            map[dateStr].push({
                title: `DistEd Hours: ${roundTwoDigits(attendance.distEdHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Make-up Hours: ${roundTwoDigits(attendance.makeUpHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Attended Hours: ${roundTwoDigits(attendance.attendedHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Scheduled Hours: ${roundTwoDigits(attendance.scheduleHours ?? 0)}`
            })
        }
        return map
    }, [dailyAttendanceSummaryData])

    const attendanceByDate = useMemo(() => {
        const dailyAttendances = dailyAttendanceSummaryData.dailyAttendances ?? []
        const map = {}
        for (const attendance of dailyAttendances) {
            const dateStr = formatDateOnly(attendance.date)
            map[dateStr] = {
                missedHours: Number(attendance.missedHours ?? 0),
                distEdHours: Number(attendance.distEdHours ?? 0),
                makeUpHours: Number(attendance.makeUpHours ?? 0),
                attendedHours: Number(attendance.attendedHours ?? 0),
                scheduleHours: Number(attendance.scheduleHours ?? 0)
            }
        }
        return map
    }, [dailyAttendanceSummaryData])

    const onCloseEditPopup = () => {
        setIsEditPopupVisible(false)
        setSelectedDateStr(null)
    }

    const onOpenEditPopup = (dateStr: string) => {
        setSelectedDateStr(dateStr)
        const existing = attendanceByDate?.[dateStr]

        if ((existing && canEdit) || (!existing && canAdd)) {
            setEditValues({
                date: moment(dateStr),
                missedHours: Number(existing?.missedHours ?? 0),
                distEdHours: Number(existing?.distEdHours ?? 0),
                makeUpHours: Number(existing?.makeUpHours ?? 0),
                attendedHours: Number(existing?.attendedHours ?? 0),
                scheduleHours: Number(existing?.scheduleHours ?? 0)
            })
            setIsEditPopupVisible(true)
        }
    }

    const onConfirmEditPopup = async () => {
        try {
            setIsSubmitting(true)

            await attendanceService.createOrUpdateManualDailyAttendanceBydate({
                attendances: [
                    {
                        profileId: Number(studentProfileId),
                        date: moment(editValues.date).format("YYYY-MM-DD"),
                        attendedHours: Number(editValues.attendedHours ?? 0),
                        scheduleHours: Number(editValues.scheduleHours ?? 0),
                        makeUpHours: Number(editValues.makeUpHours ?? 0),
                        missedHours: Number(editValues.missedHours ?? 0),
                        distEdHours: Number(editValues.distEdHours ?? 0),
                        campusId
                    }
                ]
            })

            refetch()
            setIsEditPopupVisible(false)
            setSelectedDateStr(null)
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.pickerWrap}>
                <BaseRangePicker
                    placeholder={["Date From", "Date To"]}
                    isClearable
                    value={dateRange?.length === 2 ? [moment(dateRange[0]), moment(dateRange[1])] : null}
                    onChange={(value) => {
                        if (value) {
                            setDateRange([moment(value[0]), moment(value[1])])
                        } else {
                            setDateRange([])
                        }
                    }}
                />
            </div>
            {dateRange?.length === 2 ? (
                <>
                    <div className={styles.monthsWrap}>
                        {monthsInRange.map((monthMoment) => {
                            const year = monthMoment.year()
                            const month = monthMoment.month()
                            const monthName = monthMoment.format("MMMM YYYY")
                            const days = buildMonthDays(monthMoment)

                            return (
                                <div key={`${year}-${month}`} className={styles.monthSection}>
                                    <h2 className={styles.monthTitle}>{monthName}</h2>
                                    <div className={styles.calendarGrid}>
                                        {/* Weekday headers */}
                                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                                            <div key={d} className={styles.weekdayHeader}>
                                                {d}
                                            </div>
                                        ))}

                                        {/* Day cells */}
                                        {days.map((day, idx) => {
                                            if (!day) {
                                                return <div key={idx} className={styles.emptyCell} />
                                            }

                                            const dateStr = moment([year, month, day]).format("YYYY-MM-DD")
                                            const events = eventsByDate[dateStr] || []
                                            const isInRange =
                                                dateRange?.length === 2 &&
                                                moment(dateStr).isBetween(
                                                    moment(dateRange[0]),
                                                    moment(dateRange[1]),
                                                    "day",
                                                    "[]"
                                                )
                                            const isHovered = isInRange && hoveredDateStr === dateStr

                                            return (
                                                <div
                                                    key={idx}
                                                    className={cx(styles.eventContainer, {
                                                        [styles.eventContainerDisabled]: !isInRange,
                                                        [styles.eventContainerHover]: isHovered,
                                                        [styles.eventContainerSelected]:
                                                            isInRange && selectedDateStr === dateStr
                                                    })}
                                                    onMouseEnter={() => setHoveredDateStr(dateStr)}
                                                    onMouseLeave={() => setHoveredDateStr(null)}
                                                    onClick={() => {
                                                        if (!isInRange) return
                                                        onOpenEditPopup(dateStr)
                                                    }}>
                                                    <div className={styles.day}>{day}</div>
                                                    <div className={styles.eventList}>
                                                        {isInRange &&
                                                            events.map((evt, i) => (
                                                                <div
                                                                    key={i}
                                                                    className={styles.eventItem}
                                                                    title={evt.title}>
                                                                    {evt.title}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <SummaryView dailyAttendanceSummaryData={dailyAttendanceSummaryData} />
                </>
            ) : (
                <div className={styles.missingDateRangeMessage}>
                    Please select a date range to view the weekly attendance records.
                </div>
            )}

            <BasePopup
                isShow={isEditPopupVisible}
                onClose={onCloseEditPopup}
                width="520px"
                leftIcon="EDIT"
                leftIconColor="#fff">
                <div className={styles.popupRoot}>
                    <div className={styles.popupTitle}>
                        {attendanceByDate?.[selectedDateStr] ? "Update Daily Attendance" : "Create Daily Attendance"}
                    </div>
                    <Row gutter={[12, 12]} className={styles.popupForm}>
                        <Col span={24}>
                            <FormLabel label="Date" />
                            <BaseDatePicker value={editValues.date} disabled />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Missed Hours" />
                            <BaseInput
                                className={styles.inputNumber}
                                min={0}
                                type="number"
                                step={0.25}
                                value={editValues.missedHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, missedHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="DistEd Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                value={editValues.distEdHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, distEdHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Make-up Hours" />
                            <BaseInput
                                className={styles.inputNumber}
                                min={0}
                                type="number"
                                value={editValues.makeUpHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, makeUpHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Attended Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                value={editValues.attendedHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, attendedHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={24}>
                            <FormLabel label="Scheduled Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                value={editValues.scheduleHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, scheduleHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                    </Row>
                    <div className={styles.popupFooter}>
                        <div className={styles.popupButton}>
                            <SecondaryButton title="Cancel" onClick={onCloseEditPopup} disabled={isSubmitting} />
                        </div>
                        <div className={styles.popupButton}>
                            <BaseButton title="Confirm" onClick={onConfirmEditPopup} loading={isSubmitting} />
                        </div>
                    </div>
                </div>
            </BasePopup>
            <BaseLoading isShow={isFetching || isSubmitting} />
        </div>
    )
}

export default DailyAttendanceCalendarView
